// src/components/Products/Product.jsx
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import ProductList from './ProductList';
import ProductCard from './ProductCard';
import { trackSurfsideEvent } from '../Surfside/util';
import { gtmTrackEvent, trackEcommerceEvent } from '../../analytics.js';
import { useConfig } from "../../contexts/ConfigContext";
import { useAppState } from '../../contexts/AppStateContext';
import { useCart } from "../../contexts/CartContext";
import { useAddItem } from '../../hooks/dutchie-mutations.js';

const Product = ({
    product,
    display,
    isRelatedProducts,
    menuSlug,
    columns,
    initialDisplayStyle,
    prioritySpecialObject,
    allSpecials = null,
    showMultipleSpecials = false,
    isFirstProduct = false,
}) => {
    const { retailerId, setSidebarToggle } = useAppState();
    const {
        retailers,
        surfside,
        analytics,
    } = useConfig();
    const { cartData, isFetched: isCartFetched } = useCart();

    // Initialize selected variant
    const [selectedVariant, setSelectedVariant] = useState(() => 
        product.variants && product.variants.length > 0 ? product.variants[0] : null
    );

    // Initialize errors state
    const [errors, setErrors] = useState([]);

    // Memoize single product URL
    const singleProductUrl = useMemo(() => 
        `/shop/${menuSlug}/product/${product.slug}/`,
        [menuSlug, product.slug]
    );

    // Memoize variables for mutation with better validation
    const variables = useMemo(() => {
        // Don't create variables if cart isn't ready or variant isn't selected
        if (!selectedVariant?.id || !cartData?.id || !retailerId) {
            return null;
        }
        
        const [productId, option] = selectedVariant.id.split('~');
        
        return {
            retailerId,
            checkoutId: cartData.id,
            quantity: 1,
            productId,
            option,
            // Add special ID if available
            ...(prioritySpecialObject?.id && { specialId: prioritySpecialObject.id })
        };
    }, [retailerId, cartData?.id, selectedVariant?.id, prioritySpecialObject?.id]);

    // Memoize tracking callback
    const surfsideAddItemCallback = useCallback(() => {
        // Track Surfside event
        if (surfside?.enable && retailers?.[retailerId]) {
            trackSurfsideEvent(
                'add',
                product,
                selectedVariant,
                1,
                prioritySpecialObject?.id || null,
                retailerId,
                retailers[retailerId] // Fixed: use retailers instead of globalConfig
            );
        }

        // Track GTM event
        if (analytics?.gtm_id && product) {
            gtmTrackEvent(
                'add_to_cart', 
                product, 
                selectedVariant || product.variants[0], 
                1, 
                prioritySpecialObject
            );
        }

        // Track internal/Sentry event
        trackEcommerceEvent('add', product, selectedVariant, prioritySpecialObject);
    }, [
        surfside?.enable, 
        retailers, 
        retailerId, 
        product, 
        selectedVariant, 
        prioritySpecialObject, 
        analytics?.gtm_id
    ]);

    // Use add item mutation hook
    const handleAddItem = useAddItem(
        retailerId,
        cartData?.id,
        setSidebarToggle,
        setErrors,
        surfsideAddItemCallback
    );

    // Handle variant selection (for dropdowns)
    const handleVariantSelection = useCallback((variant) => {
        setSelectedVariant(variant);
    }, []);

    // Handle variant box selection (for boxes - includes immediate add to cart)
    const handleVariantBoxSelection = useCallback((variant) => {
        // Validate cart is ready before attempting to add
        if (!cartData?.id || !isCartFetched || !retailerId) {
            console.error('Cart not ready for adding items', {
                hasCartId: !!cartData?.id,
                isCartFetched,
                retailerId
            });
            setErrors(['Cart is still loading. Please try again.']);
            return;
        }

        setSelectedVariant(variant);
        
        // Create variables for this specific variant
        const [productId, option] = variant.id.split('~');
        const boxVariables = {
            retailerId,
            checkoutId: cartData.id,
            quantity: 1,
            productId,
            option,
            // Add special ID if available
            ...(prioritySpecialObject?.id && { specialId: prioritySpecialObject.id })
        };
        
        handleAddItem.mutate(boxVariables);
    }, [retailerId, cartData?.id, handleAddItem, isCartFetched, prioritySpecialObject?.id]);

    // Effect to ensure selected variant is always valid
    useEffect(() => {
        if (product?.variants?.length > 0 && !selectedVariant) {
            setSelectedVariant(product.variants[0]);
        }
    }, [product, selectedVariant]);

    // Common props for both list and card views
    const commonProps = useMemo(() => ({
        product,
        prioritySpecialObject,
        allSpecials,
        showMultipleSpecials,
        singleProductUrl,
        selectedVariant,
        setSelectedVariant,
        handleAddItem,
        handleVariantSelection,
        handleVariantBoxSelection,
        variables,
        errors,
        menuSlug,
        isCartReady: Boolean(cartData?.id && isCartFetched && retailerId),
        isFirstProduct
    }), [
        product,
        prioritySpecialObject,
        allSpecials,
        showMultipleSpecials,
        singleProductUrl,
        selectedVariant,
        handleAddItem,
        handleVariantSelection,
        handleVariantBoxSelection,
        variables,
        errors,
        menuSlug,
        cartData?.id,
        isCartFetched,
        retailerId,
        isFirstProduct
    ]);

    // Render appropriate component based on display type
    if (display === 'list') {
        return <ProductList {...commonProps} />;
    }

    return (
        <ProductCard
            {...commonProps}
            isRelatedProducts={isRelatedProducts}
            columns={columns}
            initialDisplayStyle={initialDisplayStyle}
        />
    );
};

export default Product;
